import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Eye, Download, Printer, AlertCircle } from 'lucide-react';
import { DataTable } from '../../../../components/ui/DataTable';
import { ConfirmDialog } from '../../../../components/ui/ConfirmDialog';
import { DropdownMenu } from '../../../../components/ui/DropdownMenu';
import { CopyableText } from '../../../../components/ui/CopyableText';
import { PickupStats } from './components/PickupStats';
import { PickupFilters } from './components/PickupFilters';
import { fetchPickups, reprintPickupLabel } from '../../../../features/pickups/pickupsThunks';
import { setFilters, setPage, setRowsPerPage } from '../../../../features/pickups/pickupsSlice';
import {
  formatPickupDate,
  resolvePickingState,
  getTodayRange,
} from '../../../../features/pickups/pickupsUtils';
import { fetchSellerOptions } from '../../../../features/sellers/sellersThunks';

/**
 * The day's pickings: one row per order a driver scanned at the CD.
 *
 * Worth knowing while reading this: a "pickup" here is a label record, not a
 * collection request. The driver and the route come frozen from the TMS at scan
 * time, and ESTADO is the state of the label's print job — which is why the
 * only actions are about the order behind it and about the paper.
 */
export const Pickups = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data, total, summary, isLoading, reprintingId, error, filters } = useSelector(
    (state) => state.pickups
  );
  const { sellerOptions } = useSelector((state) => state.sellers);

  const [pickupToReprint, setPickupToReprint] = useState(null);

  useEffect(() => {
    dispatch(fetchSellerOptions());
  }, [dispatch]);

  /**
   * A persisted range is yesterday's range tomorrow.
   *
   * The filters survive a reload, which is what you want for the seller, the
   * state and the search box — but a window that already closed would open the
   * page on a day that is over and show nothing, reading as an outage rather
   * than as a filter. Only a range that ended in the past is pulled forward.
   */
  const isStaleRange = Boolean(filters.end_date) && filters.end_date < getTodayRange().start_date;

  useEffect(() => {
    // Correcting and fetching live in the same effect on purpose: split in two,
    // the mount would fire one request against the stale range and a second one
    // against today.
    if (isStaleRange) {
      dispatch(setFilters(getTodayRange()));
      return;
    }

    dispatch(fetchPickups());
  }, [
    dispatch,
    isStaleRange,
    filters.start_date,
    filters.end_date,
    filters.state,
    filters.seller,
    filters.search,
    filters.page,
    filters.rowsPerPage,
  ]);

  const handleFilterChange = useCallback(
    (fieldOrUpdates, value) => {
      if (typeof fieldOrUpdates === 'object' && fieldOrUpdates !== null) {
        dispatch(setFilters(fieldOrUpdates));
      } else {
        dispatch(setFilters({ [fieldOrUpdates]: value }));
      }
    },
    [dispatch]
  );

  const handleConfirmReprint = async () => {
    const pickup = pickupToReprint;
    if (!pickup) return;

    await dispatch(reprintPickupLabel(pickup.id));
    setPickupToReprint(null);
  };

  const buildRowActions = (row) => [
    {
      label: 'Ver orden',
      icon: Eye,
      onClick: () => navigate(`/order-details/${row.order?.id}`),
      disabled: !row.order?.id,
    },
    {
      label: 'Descargar etiqueta',
      icon: Download,
      onClick: () => window.open(row.label_url, '_blank', 'noopener,noreferrer'),
      disabled: !row.has_label,
      description: row.has_label ? undefined : 'Esta orden todavía no tiene etiqueta generada',
    },
    {
      label: 'Reimprimir etiqueta',
      icon: Printer,
      onClick: () => setPickupToReprint(row),
      dividerBefore: true,
      description: 'Vuelve a enviarla a la cola de impresión',
    },
  ];

  const columns = useMemo(
    () => [
      {
        header: 'FECHA PICKING',
        cell: (row) => (
          <span className="text-xs font-medium text-text-secondary dark:text-gray-400">
            {formatPickupDate(row.created_at)}
          </span>
        ),
      },
      {
        header: 'CONDUCTOR',
        cell: (row) => row.driver_full_name || '-',
      },
      {
        header: 'ORDEN RUTA',
        cell: (row) => (
          <span className="font-semibold text-ninja-dark dark:text-white">{row.route || '-'}</span>
        ),
      },
      { header: 'ORDEN', cell: (row) => row.order?.id ?? '-' },
      { header: 'DELIVERY', cell: (row) => row.delivery?.id ?? '-' },
      {
        header: 'ESTADO',
        cell: (row) => {
          const state = resolvePickingState(row.state);
          return (
            <div
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
              style={{ backgroundColor: `${state.color}1a`, color: state.color }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: state.color }}
              />
              {state.label}
            </div>
          );
        },
      },
      {
        header: 'TRACKING',
        cell: (row) => <CopyableText value={row.delivery?.courier_tracking_number} />,
      },
      {
        header: 'SELLER',
        cell: (row) => row.seller?.company?.social_reason || '-',
      },
      {
        header: '',
        cell: (row) => <DropdownMenu items={buildRowActions(row)} />,
      },
    ],
    // `buildRowActions` closes over `navigate` and `dispatch`, both stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className="space-y-6">
      <PickupStats summary={summary} />

      <PickupFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        sellerOptions={sellerOptions}
      />

      {error && (
        <div className="flex items-center gap-2 bg-red-50 dark:bg-red-900/20 border border-danger/30 text-danger rounded-xl px-4 py-3 text-sm font-medium">
          <AlertCircle size={16} className="flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="flex flex-col gap-4">
        <h2 className="text-base font-bold text-ninja-dark dark:text-white">Recogidas del día</h2>

        <DataTable
          columns={columns}
          data={data}
          totalItems={total}
          currentPage={filters.page}
          itemsPerPage={filters.rowsPerPage}
          isLoading={isLoading}
          onPageChange={(page) => dispatch(setPage(page))}
          onRowsPerPageChange={(rowsPerPage) => dispatch(setRowsPerPage(rowsPerPage))}
          itemLabel="pickups"
        />
      </div>

      <ConfirmDialog
        isOpen={Boolean(pickupToReprint)}
        onClose={() => setPickupToReprint(null)}
        onConfirm={handleConfirmReprint}
        title="Reimprimir etiqueta"
        message={
          pickupToReprint
            ? `Se generará de nuevo la etiqueta de la orden ${pickupToReprint.order?.id} y se enviará a la impresora. Saldrá papel: no se puede deshacer.`
            : ''
        }
        confirmLabel="Reimprimir"
        loadingLabel="Enviando a la cola..."
        loading={Boolean(reprintingId)}
      />
    </div>
  );
};
